import { useCallback, useEffect, useState } from 'react';
import { Plus, X } from 'lucide-react';
import Card from '../ui/Card';
import Button from '../ui/Button';
import Modal from '../ui/Modal';
import { Skeleton } from '../Skeleton';
import {
    listClientProfiles,
    upsertClientProfile,
    type ClientProfile,
} from '../../mentorshipService';

function ChipList({
    label,
    items,
    setItems,
}: {
    label: string;
    items: string[];
    setItems: (v: string[]) => void;
}) {
    const [draft, setDraft] = useState('');
    return (
        <div>
            <label>{label}</label>
            <div className="report-submit__chips">
                {items.map((item, i) => (
                    <span key={i} className="mentorship-chip mentorship-chip--module">
                        {item}
                        <button
                            type="button"
                            aria-label={`Remove ${item}`}
                            onClick={() => setItems(items.filter((_, j) => j !== i))}
                        >
                            <X size={12} />
                        </button>
                    </span>
                ))}
            </div>
            <div className="report-submit__chip-input">
                <input
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            if (draft.trim()) {
                                setItems([...items, draft.trim()]);
                                setDraft('');
                            }
                        }
                    }}
                />
            </div>
        </div>
    );
}

function ClientFormModal({
    onClose,
    onSaved,
}: {
    onClose: () => void;
    onSaved: (cp: ClientProfile) => void;
}) {
    const [clientId, setClientId] = useState('');
    const [priorities, setPriorities] = useState<string[]>([]);
    const [behaviors, setBehaviors] = useState<string[]>([]);
    const [advice, setAdvice] = useState<string[]>([]);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const submit = async () => {
        if (!clientId.trim()) return;
        setSaving(true);
        setError(null);
        try {
            const cp = await upsertClientProfile({
                client_id: clientId.trim(),
                priorities,
                behaviors,
                management_advice: advice,
            });
            onSaved(cp);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Save failed');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal open onClose={onClose} size="md" ariaLabel="Client profile">
            <Modal.Header onClose={onClose}>Client profile</Modal.Header>
            <Modal.Body>
                <label>Client id</label>
                <input value={clientId} onChange={(e) => setClientId(e.target.value)} />
                <ChipList label="Priorities" items={priorities} setItems={setPriorities} />
                <ChipList label="Behaviors" items={behaviors} setItems={setBehaviors} />
                <ChipList label="Management advice" items={advice} setItems={setAdvice} />
                {error && <p className="report-submit__error">{error}</p>}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="ghost" size="md" onClick={onClose}>Cancel</Button>
                <Button variant="primary" size="md" onClick={submit} disabled={saving}>
                    {saving ? 'Saving…' : 'Save'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

function MentorClientProfilesEditor() {
    const [profiles, setProfiles] = useState<ClientProfile[] | null>(null);
    const [loading, setLoading] = useState(true);
    const [showNew, setShowNew] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const page = await listClientProfiles();
            setProfiles(page.results);
        } catch {
            setProfiles([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        void load();
    }, [load]);

    return (
        <div>
            <div className="mentor-projects__header">
                <h3>Client profiles</h3>
                <Button
                    variant="primary"
                    size="md"
                    icon={<Plus size={16} />}
                    onClick={() => setShowNew(true)}
                >
                    New profile
                </Button>
            </div>
            {loading ? (
                <Skeleton width="100%" height="80px" borderRadius="12px" />
            ) : !profiles || profiles.length === 0 ? (
                <div className="empty-state">
                    <h3>No client profiles</h3>
                    <p>Curate your first client to share knowledge with mentees.</p>
                </div>
            ) : (
                <ul>
                    {profiles.map((p) => (
                        <li key={p.id}>
                            <Card variant="row">
                                <Card.Header>
                                    <div>
                                        <p><strong>{p.client_id}</strong></p>
                                        <p>{p.priorities.length} priorities · {p.behaviors.length} behaviors</p>
                                    </div>
                                </Card.Header>
                            </Card>
                        </li>
                    ))}
                </ul>
            )}
            {showNew && (
                <ClientFormModal
                    onClose={() => setShowNew(false)}
                    onSaved={(cp) => {
                        setShowNew(false);
                        setProfiles((prev) => [cp, ...(prev ?? [])]);
                    }}
                />
            )}
        </div>
    );
}

export default MentorClientProfilesEditor;
